import api from './api';

export const documentService = {
  // Get organized structure (cases list, templates folder, personal folder)
  getOrganized: async () => {
    const response = await api.get('/documents/organized');
    return response.data;
  },

  // List documents with optional filters
  listDocuments: async (params = {}) => {
    // params: { scope, case_id, category, search, limit, offset }
    const response = await api.get('/documents/', { params });
    return response.data;
  },

  // Get documents for a specific case
  getCaseDocuments: async (caseId, category = null, search = null) => {
    const params = {}; if (category) params.category = category; if (search) params.search = search;
    const response = await api.get(`/documents/case/${caseId}`, { params });
    return response.data;
  },

  // Get folders for a specific case
  getCaseFolders: async (caseId) => {
    const response = await api.get(`/documents/case/${caseId}/folders`);
    return response.data;
  },

  // Get contents of a folder (subfolders and documents)
  getFolderContents: async (folderId) => {
    const response = await api.get(`/documents/folder/${folderId}`);
    return response.data;
  },

  // Upload document
  uploadDocument: async (file, scope, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', scope); // 'case', 'template', 'personal'

    if (options.caseId) {
      formData.append('case_id', options.caseId);
    }
    if (options.category) {
      formData.append('category', options.category);
    }
    if (options.description) {
      formData.append('description', options.description);
    }

    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Download document
  downloadDocument: async (docId, filename) => {
    const response = await api.get(`/documents/${docId}/download`, {
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Download document as blob for preview (no download prompt)
  downloadDocumentBlob: async (docId) => {
    const response = await api.get(`/documents/${docId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get preview URL for document (creates object URL from blob)
  getPreviewUrl: async (docId) => {
    const response = await api.get(`/documents/${docId}/download`, {
      responseType: 'blob',
    });
    return window.URL.createObjectURL(response.data);
  },

  // Delete document (soft delete; reason required, admin-recoverable)
  deleteDocument: async (docId, reason) => {
    const response = await api.delete(`/documents/${docId}`, { data: { reason } });
    return response.data;
  },

  // Move a case document to another case (flags carry; audit-logged)
  moveDocument: async (docId, caseId) => {
    const response = await api.post(`/documents/${docId}/move`, { case_id: caseId });
    return response.data;
  },

  // Restore a soft-deleted document (admin only)
  restoreDocument: async (docId) => {
    const response = await api.post(`/documents/${docId}/restore`);
    return response.data;
  },

  // Deleted documents for a case (admin only)
  getDeletedCaseDocuments: async (caseId) => {
    const response = await api.get(`/documents/case/${caseId}/deleted`);
    return response.data;
  },

  // Same-case duplicate sets (retroactive cleanup view)
  getCaseDuplicates: async (caseId) => {
    const response = await api.get(`/documents/case/${caseId}/duplicates`);
    return response.data;
  },

  // Multi-file upload — per-file success/failure in response.results
  // Multi-file upload — chunked client-side so a large drop (a divorce
  // financial-disclosure dump is easily 20-50 files / >100MB) never lands as one
  // oversized request against the 100MB nginx/Cloudflare body cap (which returns
  // an opaque 413 that surfaced as identical useless errors on every file).
  // Files are split into sub-batches bounded by count AND cumulative bytes; each
  // sub-batch hits the SAME /documents/upload-batch endpoint; per-file results
  // are aggregated. Every failure carries a human-readable reason.
  uploadDocumentsBatch: async (files, scope, options = {}, onProgress = null) => {
    const MB = 1024 * 1024;
    const MAX_FILES_PER_REQ = 8;
    const MAX_BYTES_PER_REQ = 40 * MB;   // well under the 100MB proxy/CF cap
    const MAX_SINGLE_FILE = 90 * MB;     // a single file that can't fit under the cap

    const all = Array.from(files || []);
    const results = [];

    // Pre-flight: a single file bigger than the cap can't be chunked around.
    const sendable = [];
    for (const f of all) {
      if (f.size > MAX_SINGLE_FILE) {
        results.push({
          filename: f.name, success: false,
          error: `file too large: ${(f.size / MB).toFixed(1)}MB exceeds the ${MAX_SINGLE_FILE / MB}MB per-file limit`,
        });
      } else {
        sendable.push(f);
      }
    }

    // Pack sendable files into sub-batches (count + cumulative-bytes bounded).
    const chunks = [];
    let cur = [];
    let curBytes = 0;
    for (const f of sendable) {
      if (cur.length >= MAX_FILES_PER_REQ || (cur.length && curBytes + f.size > MAX_BYTES_PER_REQ)) {
        chunks.push(cur);
        cur = [];
        curBytes = 0;
      }
      cur.push(f);
      curBytes += f.size;
    }
    if (cur.length) chunks.push(cur);

    const totalBytes = sendable.reduce((s, f) => s + f.size, 0) || 1;
    let sentBytes = 0;

    for (const chunk of chunks) {
      const formData = new FormData();
      chunk.forEach((f) => formData.append('files', f));
      formData.append('scope', scope);
      if (options.caseId) formData.append('case_id', options.caseId);
      if (options.category) formData.append('category', options.category);
      if (options.description) formData.append('description', options.description);
      if (options.override) formData.append('override_duplicates', 'true');
      const chunkBytes = chunk.reduce((s, f) => s + f.size, 0);
      try {
        const resp = await api.post('/documents/upload-batch', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: onProgress
            ? (e) => {
                const within = e.total ? (e.loaded / e.total) * chunkBytes : chunkBytes;
                onProgress(Math.min(100, Math.round(((sentBytes + within) / totalBytes) * 100)));
              }
            : undefined,
        });
        (resp.data.results || []).forEach((r) => results.push(r));
      } catch (err) {
        // Whole sub-batch failed (413 body cap, network, auth) — surface a
        // human-readable reason per file in the chunk, never a bare code.
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        let reason;
        if (status === 413) {
          reason = `upload rejected: this batch (${(chunkBytes / MB).toFixed(1)}MB) exceeded the server's request-size limit`;
        } else if (detail) {
          reason = detail;
        } else if (status) {
          reason = `upload failed (HTTP ${status})`;
        } else {
          reason = err.message || 'upload failed (network error)';
        }
        chunk.forEach((f) => results.push({ filename: f.name, success: false, error: reason }));
      }
      sentBytes += chunkBytes;
      if (onProgress) onProgress(Math.min(100, Math.round((sentBytes / totalBytes) * 100)));
    }

    const succeeded = results.filter((r) => r.success).length;
    return { results, succeeded, failed: results.length - succeeded };
  },

  // Rename document
  renameDocument: async (docId, newName) => {
    const response = await api.put(`/documents/${docId}/rename`, null, {
      params: { new_name: newName }
    });
    return response.data;
  },

  // Search documents
  searchDocuments: async (query, scope = null) => {
    const params = { q: query };
    if (scope) {
      params.scope = scope;
    }
    const response = await api.get('/documents/search', { params });
    return response.data;
  },

  // Format file size for display
  formatFileSize: (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  },

  // Get file icon based on mime type
  getFileIcon: (mimeType) => {
    if (!mimeType) return 'file';

    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'word';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'excel';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'powerpoint';
    if (mimeType.includes('zip') || mimeType.includes('archive')) return 'archive';
    if (mimeType.startsWith('text/')) return 'text';

    return 'file';
  },
};
